// DEM-45 — pure message-conversion layer for the assistant-ui external store.
// Ported from the Erez/template dashboard (MJOS-147). One small addition for
// the proactive on-load behaviour: messages can be flagged `__hidden` so the
// seed user prompt that triggers the opening sweep never renders in the UI.
//
// Two pure, unit-testable functions sit between pi-agent-core's transcript and
// assistant-ui's renderer:
//
//   toDisplayMessages(messages, runningId?, cache?, hiddenContents?)
//     Folds each standalone pi `toolResult` message into the matching
//     `toolCall` part of the preceding assistant message (matched on
//     toolCallId), attaching its text + isError as the part's `result`, then
//     DROPS the standalone toolResult. The result is a transcript where every
//     tool call carries its own result — which is exactly the shape
//     assistant-ui renders inline on the assistant turn. It also stamps a
//     stable `id` on every message (the message's own id if pi provides one,
//     otherwise `committed-${index}`) and a `status`: the message whose id
//     matches `runningId` is `{type:"running"}`, everything else is
//     `{type:"complete"}`.
//
//     If `hiddenContents` is provided, any user message whose extracted text
//     exactly matches one of the strings is dropped from the display list.
//     This is how the proactive seed prompt stays invisible to the user.
//
//   convertMessage(message)
//     Maps ONE enriched pi AgentMessage → assistant-ui ThreadMessageLike.
//     Stable module-level fn (so useExternalMessageConverter can memo-cache by
//     object identity). Defensive: never throws.
import type { ThreadMessageLike } from "@assistant-ui/react";
import type { AgentMessage } from "@earendil-works/pi-agent-core";

type ThreadMessagePart = Exclude<ThreadMessageLike["content"], string>[number];

/** An AgentMessage decorated with the display id + running/complete status. */
export type DisplayMessage = AgentMessage & {
	__displayId: string;
	__running: boolean;
};

export type DisplayCache = WeakMap<object, CacheEntry>;
interface CacheEntry {
	decorated: DisplayMessage;
	running: boolean;
	resultKey: string;
}

export function createDisplayCache(): DisplayCache {
	return new WeakMap();
}

function textOf(content: unknown): string {
	if (typeof content === "string") return content;
	if (Array.isArray(content)) {
		return content
			.filter((c) => (c as { type?: string })?.type === "text")
			.map((c) => String((c as { text?: unknown }).text ?? ""))
			.join("\n")
			.trim();
	}
	return "";
}

export function toDisplayMessages(
	messages: AgentMessage[],
	runningId?: string,
	cache?: DisplayCache,
	hiddenContents?: ReadonlySet<string>,
): DisplayMessage[] {
	// Pass 1: collect tool-call ownership + per-owner result bags.
	const ownerByToolCallId = new Map<string, object>();
	const resultsFor = new Map<
		object,
		Map<string, { result: unknown; isError: boolean }>
	>();

	for (const message of messages) {
		const raw = message as unknown as Record<string, unknown>;
		if (raw?.role === "toolResult") {
			const toolCallId = String(raw.toolCallId ?? "");
			const owner = toolCallId ? ownerByToolCallId.get(toolCallId) : undefined;
			if (owner) {
				const text = textOf(raw.content);
				let bag = resultsFor.get(owner);
				if (!bag) resultsFor.set(owner, (bag = new Map()));
				bag.set(toolCallId, {
					result: text || raw.content,
					isError: !!raw.isError,
				});
			}
			continue;
		}
		const content = raw?.content;
		if (Array.isArray(content)) {
			for (const p of content) {
				const pp = p as Record<string, unknown>;
				if (pp?.type === "toolCall") {
					const id = String(pp.id ?? "");
					if (id) ownerByToolCallId.set(id, message as object);
				}
			}
		}
	}

	// Pass 2: decorate non-toolResult messages, dropping toolResults and any
	// user message whose text exactly matches a `hiddenContents` entry.
	const out: DisplayMessage[] = [];
	for (let i = 0; i < messages.length; i++) {
		const message = messages[i];
		const raw = message as unknown as Record<string, unknown>;
		if (raw?.role === "toolResult") continue;
		if (
			raw?.role === "user" &&
			hiddenContents &&
			hiddenContents.size > 0 &&
			hiddenContents.has(textOf(raw.content))
		) {
			continue;
		}

		// [MJ-393] Final-message-only render. An assistant message that carries
		// a toolCall part is a mid-run step (the tool invocation plus whatever
		// planning prose rode along with it). Drop the whole turn so the user
		// never sees tool chips OR intermediate planning — only the final
		// natural-language answer, which the model emits as its own tool-free
		// message after the results come back. Folded tool results (from pass 1)
		// simply go unused. Guards against Scout-style leaks even on Kimi.
		if (raw?.role !== "user") {
			const c = raw?.content;
			if (
				Array.isArray(c) &&
				c.some((p) => (p as { type?: string })?.type === "toolCall")
			) {
				continue;
			}
		}

		const displayId = String(raw.id ?? `committed-${i}`);
		const running = displayId === runningId;
		const bag = resultsFor.get(message as object);
		const resultKey = bag
			? JSON.stringify(
					[...bag.entries()].map(([id, r]) => [id, r.isError, r.result]),
				)
			: "";

		const cached = cache?.get(message as object);
		if (cached && cached.running === running && cached.resultKey === resultKey) {
			out.push(cached.decorated);
			continue;
		}

		const cloned: Record<string, unknown> = { ...raw };
		const content = raw.content;
		if (Array.isArray(content)) {
			cloned.content = content.map((p) => {
				if (!p || typeof p !== "object") return p;
				const copy = { ...(p as object) } as Record<string, unknown>;
				if (copy.type === "toolCall" && bag) {
					const r = bag.get(String(copy.id ?? ""));
					if (r) {
						copy.result = r.result;
						if (r.isError) copy.isError = true;
					}
				}
				return copy;
			});
		}
		const decorated = cloned as unknown as DisplayMessage;
		decorated.__displayId = displayId;
		decorated.__running = running;
		out.push(decorated);
		cache?.set(message as object, { decorated, running, resultKey });
	}

	return out;
}

export function convertMessage(message: AgentMessage): ThreadMessageLike {
	const m = message as unknown as Record<string, unknown>;
	const rawRole = m?.role;

	const role: "assistant" | "user" | "system" =
		rawRole === "user" ? "user" : rawRole === "system" ? "system" : "assistant";

	const id = typeof m.__displayId === "string" ? m.__displayId : undefined;
	const status: ThreadMessageLike["status"] | undefined =
		role === "assistant"
			? m.__running
				? { type: "running" }
				: { type: "complete", reason: "stop" }
			: undefined;

	const content = m?.content;

	if (typeof content === "string") {
		return {
			role,
			id,
			...(status ? { status } : {}),
			content: content ? [{ type: "text", text: content }] : [],
		};
	}

	if (Array.isArray(content)) {
		const parts: ThreadMessagePart[] = [];
		for (const part of content) {
			const p = part as Record<string, unknown>;
			switch (p?.type) {
				case "text":
					parts.push({ type: "text", text: String(p.text ?? "") });
					break;
				case "thinking":
					if (p.thinking)
						parts.push({ type: "reasoning", text: String(p.thinking) });
					break;
				case "toolCall":
					// [MJ-393] Never render tool calls. toDisplayMessages already
					// drops whole tool-bearing turns; this is a defensive belt so a
					// stray toolCall part can't paint a chip either.
					break;
				case "image":
					if (typeof p.data === "string") {
						parts.push({
							type: "image",
							image: p.mimeType
								? `data:${p.mimeType};base64,${p.data}`
								: String(p.data),
						});
					}
					break;
				default:
					// [MJ-393] Unknown/unsupported part — DROP it. Never
					// JSON.stringify into a visible text part: that is exactly what
					// leaked raw `{"name":"list_my_stations","parameters":{}}` tool-call
					// JSON into the chat bubble when a model wrote a call as an
					// unexpected content shape. Silence beats leaking internals.
					break;
			}
		}
		return { role, id, ...(status ? { status } : {}), content: parts };
	}

	return { role, id, ...(status ? { status } : {}), content: [] };
}
